import {
    ApplicationRoleCode,
    CorporationUserRole,
    EdgeComputeOwnerType,
    FeatureUserGroup,
    GroupType,
    HardwareStatusCode,
    ICorporationResponse,
    ICurrentUserResponse,
    IDashboardResponse,
    IEdgeDefinitionResponse,
    IEdgeImageRepositoryResponse,
    IIssueActivityUserChild,
    ILocationResponse,
    IssueStatusType,
    IUserResponse,
    LocationUserRole,
    MachineHealthEventType,
    UserCorporationChild,
    UserLocationChild,
} from '@kcftech/prometheus-client';
import { PermissionsContext } from 'contexts/PermissionsContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useUser } from 'contexts/UserContext';
import { allowedHealthRangeIssueStatuses } from 'pages/Desk/issues/issue_modals/shared';
import { useEffect, useMemo, useState } from 'react';
import { isNodeInactive } from 'utils/node';
import { sentryAddBreadcrumb, sentryCaptureMessage } from 'utils/sentry';

/**
 * Gets the user's role for the current corporation
 */
export function getCurrentCorporationRole(
    corporations: UserCorporationChild[],
    corporationId: string | undefined,
) {
    return corporations.find((c) => c.CorporationId === corporationId)
        ?.CorporationUserRole;
}

/**
 * Gets the user's role for the current location
 */
export function getCurrentLocationRole(
    locations: UserLocationChild[],
    locationId: string | undefined,
) {
    return locations.find((l) => l.LocationId === locationId)?.LocationUserRole;
}

const checkIsKcf = (
    user: ICurrentUserResponse | IUserResponse | IIssueActivityUserChild | undefined,
) => {
    if (!user) {
        return false;
    }
    const globalRole = (() => {
        if ('ApplicationRole' in user) {
            return user.ApplicationRole;
        }
        if ('ApplicationRoleCode' in user) {
            return user.ApplicationRoleCode;
        }
        return undefined;
    })();
    const isGlobalAdmin = globalRole === ApplicationRoleCode.GlobalAdmin;
    const isSentry = globalRole === ApplicationRoleCode.Sentry;
    const isSales = globalRole === ApplicationRoleCode.Sales;
    return isGlobalAdmin || isSentry || isSales;
};

/**
 * Checks if the user has an admin role at the supplied corporation
 * @param user the current user
 * @param corporationId the corporation ID to check the role at
 * @returns true if the user as an admin role at the corporation, undefined otherwise
 */
const hasCorporationAdminRole = (
    user: ICurrentUserResponse | undefined,
    corporationId: string | undefined,
) =>
    user?.Corporations.find(({ CorporationId }) => CorporationId === corporationId)
        ?.CorporationUserRole === CorporationUserRole.Admin;

/**
 * Checks if the user has an admin role at the supplied location
 * @param user the current user
 * @param locationId the location ID to check the role at
 * @returns true if the user as an admin role at the location, undefined otherwise
 */
const hasLocationAdminRole = (
    user: ICurrentUserResponse | undefined,
    locationId: string | undefined,
) =>
    user?.Locations.find(({ LocationId }) => LocationId === locationId)
        ?.LocationUserRole === LocationUserRole.Admin;

export function getUserPermissions(
    user: ICurrentUserResponse | undefined,
    location: ILocationResponse | undefined,
    corporation: ICorporationResponse | undefined,
    { adminCorporationIds, adminLocationIds }: UseOrganizationPermissionsReturn,
    isDeskChatUser: (userId: string) => boolean,
): PermissionsContext {
    const globalRole = user?.ApplicationRole;
    const currentLocationId = location?.Id;
    const currentCorporationId = corporation?.Id;

    const currentLocationRole = getCurrentLocationRole(
        user?.Locations ?? [],
        currentLocationId,
    );
    const hasRoleAtLocation = currentLocationRole !== undefined;
    const currentCorporationRole = getCurrentCorporationRole(
        user?.Corporations ?? [],
        currentCorporationId,
    );
    const hasRoleAtCorporation =
        currentCorporationRole !== undefined &&
        currentCorporationRole !== CorporationUserRole.None;

    const isGlobalAdmin = globalRole === ApplicationRoleCode.GlobalAdmin;
    const isSentry = globalRole === ApplicationRoleCode.Sentry;
    const isGlobalAdminOrSentry = isGlobalAdmin || isSentry;
    const isKcf = checkIsKcf(user);
    const isCorporationAdmin = currentCorporationRole === CorporationUserRole.Admin;
    const isLocationAdmin = currentLocationRole === LocationUserRole.Admin;

    const isAdmin = isGlobalAdminOrSentry || isCorporationAdmin || isLocationAdmin;
    const canViewFaultyHardwareReport =
        isKcf || location?.LocationPermissions.IsFaultyHardwareReportEnabled === true;

    const permissionsContext: PermissionsContext = {
        canViewMissionControl: isGlobalAdminOrSentry,
        canViewDebug: isKcf || hasRoleAtCorporation || hasRoleAtLocation,
        canEnableFeatureFlags: true,
        canViewAssetMetadata: isGlobalAdminOrSentry || isCorporationAdmin,
        canViewAssemblyMetadata: isGlobalAdminOrSentry || isCorporationAdmin,
        canViewBaseline: isKcf || hasRoleAtCorporation || hasRoleAtLocation,
        canEditBaseline: isAdmin,
        canCreateTreeItemLegacy: (item, isCollectorDependentSystem) => {
            if (
                item.type === 'IndicatorGroup' ||
                item.type === 'AnalyticModel' ||
                isCollectorDependentSystem
            ) {
                return false;
            }
            return isAdmin;
        },
        canEditTreeItemLegacy: (item, isCollectorDependentSystem) => {
            if (
                item.type === 'AnalyticModel' ||
                (item.parent !== undefined && isCollectorDependentSystem)
            ) {
                return false;
            }
            return isAdmin;
        },
        canDeleteTreeItemLegacy: (item, isCollectorDependentSystem) => {
            if (
                item.type === 'AnalyticModel' ||
                (item.parent !== undefined && isCollectorDependentSystem)
            ) {
                return false;
            }
            return isAdmin;
        },
        canCreateTreeItem: ({ treeItem, system, timeSeriesCutoverPhase2 }) =>
            isAdmin &&
            !system.IsCollectorDependentSystem &&
            treeItem.TreeItemType !== GroupType.IndicatorGroup &&
            treeItem.TreeItemType !== GroupType.AnalyticModel &&
            (!timeSeriesCutoverPhase2 ||
                treeItem.TreeItemType !== GroupType.MonitoringPoint),
        canEditTreeItem: ({ treeItem, system }) =>
            isAdmin &&
            treeItem.TreeItemType !== GroupType.AnalyticModel &&
            (!system.IsCollectorDependentSystem ||
                treeItem.TreeItemType === GroupType.System),
        canDeleteTreeItem: ({ treeItem, system }) => {
            if (
                treeItem.TreeItemType === GroupType.System ||
                system.IsCollectorDependentSystem
            ) {
                return isGlobalAdminOrSentry;
            }
            return isAdmin && treeItem.TreeItemType !== GroupType.AnalyticModel;
        },
        canAssignModel: ({ treeItem, system }) =>
            isGlobalAdminOrSentry &&
            !system.IsCollectorDependentSystem &&
            treeItem.TreeItemType !== GroupType.AnalyticModel &&
            treeItem.TreeItemType !== GroupType.IndicatorGroup,
        canViewSettings: true,
        canViewReports: true,
        canViewGlobalReports: isGlobalAdminOrSentry, // canViewDraftIssuesReport or canViewRmaSensorReport
        canViewDemo: isGlobalAdmin,
        canViewCorporationDashboards: isKcf || hasRoleAtCorporation,
        canViewLocationWorkbench:
            isKcf || hasRoleAtLocation || hasRoleAtCorporation || isCorporationAdmin,
        canViewLocationDashboards:
            isKcf || hasRoleAtLocation || hasRoleAtCorporation || isCorporationAdmin,
        canCreateDashboards: isGlobalAdminOrSentry,
        canViewDashboardActions: isGlobalAdminOrSentry,
        canEditDashboard: (dashboard: IDashboardResponse) =>
            !dashboard.IsSentryManaged || isGlobalAdminOrSentry,
        canEditDashboardPermissions: isGlobalAdminOrSentry,
        canDeleteDashboard: (dashboard: IDashboardResponse) =>
            !dashboard.IsSentryManaged || isGlobalAdminOrSentry,
        canEditPowerBiReportDashboards: isGlobalAdminOrSentry,
        canEditMonitoringPoint: isAdmin,
        canEditNodeSettings: isAdmin,
        canViewTemplates: isAdmin,
        canSetFrequentUpdateIntervals: isGlobalAdmin,
        canEditIndicators: isAdmin,
        canAddIndicator: (timeSeriesCutoverPhase2) => !timeSeriesCutoverPhase2 && isAdmin,
        // No one can delete global filters. Users can delete their own filters, and admins can delete location filters.
        canEditFilter: (filter) =>
            // The filter is a user or location level filter
            (filter.locationId !== null || filter.userId !== null) &&
            // The user is an admin or the owner of the filter
            (isAdmin || filter.userId === user?.Id),
        canViewModel: isGlobalAdminOrSentry,
        canEditModelInEditor: isGlobalAdmin,
        canViewGlobalSearchIcon: true,
        canEditActivityEvent: isKcf || hasRoleAtCorporation || hasRoleAtLocation,
        canViewUsers:
            isKcf || (isAdmin && (corporation !== undefined || location !== undefined)),
        canViewTimeRangesSettings: isGlobalAdmin,
        canViewThirdPartySensors: isGlobalAdmin,
        canViewSetupTools: isGlobalAdminOrSentry,
        canViewBearingsSettings: true,
        canAddBearings: isGlobalAdminOrSentry,
        canViewLocationsSettings: isGlobalAdminOrSentry,
        canViewV4SensorSettings: isAdmin,
        canEditV4SensorSettings: isAdmin,
        canViewIdentityProviders: isGlobalAdmin,
        canEditTree: isAdmin,
        canEditCustomFilters: isAdmin,
        canAddSentry: isGlobalAdminOrSentry,
        canAddSales: isGlobalAdminOrSentry,
        canAddGlobalAdmins: isGlobalAdmin,
        canAddUsers: isAdmin,
        canEditUserProperties: isGlobalAdmin,
        canAdminEditUserProperties: isGlobalAdminOrSentry,
        canEditUserPersonas: !isKcf,
        canEditUserLocationAssignment: (corporationId, locationId) =>
            isGlobalAdminOrSentry ||
            ((adminLocationIds?.has(locationId) ||
                adminCorporationIds?.has(corporationId)) ??
                false),
        canEditUserCorporationAssignment: (corporationId) =>
            isGlobalAdminOrSentry || (adminCorporationIds?.has(corporationId) ?? false),
        canEditLocationMap: isAdmin,
        canCreateCorporation: isGlobalAdmin,
        canViewCorporationsSettings: isKcf,
        canEditCorporationSettings: isGlobalAdmin,
        canEditCorporationIcon: isKcf,
        canEditCorporationMap:
            (isCorporationAdmin && corporation !== undefined) || isGlobalAdminOrSentry,
        canViewCorporationMap: hasRoleAtCorporation || isKcf,
        canControlOtherUsersNotificationSubscriptions: isAdmin,
        canViewHardware: true,
        canEditBaseStationName: isAdmin,
        canRebootBaseStation: isAdmin,
        canEditRepeaterName: isAdmin,
        canViewFirmwareVersion: isGlobalAdminOrSentry,
        canViewSensorAge: isKcf,
        canEditMetadata: isAdmin,
        canViewFileLibrary: hasRoleAtCorporation || hasRoleAtLocation || isKcf,
        canUploadToFileLibrary: isAdmin,
        canEditFileLibrary: isAdmin,
        canDeleteFromFileLibrary: isAdmin,
        canEditAssetDetails: isAdmin,
        canSetSystemVisibility: isGlobalAdminOrSentry,
        canCreateSystem: isAdmin,
        canGenerateAlarmThresholds: isAdmin,
        canEditSystem: isAdmin,
        canDeleteSystem: isGlobalAdminOrSentry,
        canCreateDynamicSystem: isGlobalAdminOrSentry,
        canEditFileDetails: isAdmin,
        canUnlockUser: isGlobalAdmin,
        canEditLocationDetails: isGlobalAdmin,
        canEditLocationSubscriptionService: isGlobalAdminOrSentry,
        canViewLocationDetails: isGlobalAdminOrSentry,
        canEditLocationValueUnit: isGlobalAdmin,
        canViewAssetTypeSettings: isGlobalAdminOrSentry,
        canEditAssetType: isGlobalAdmin,
        canViewAssemblyTypeSettings: isGlobalAdminOrSentry,
        canEditAssemblyType: isGlobalAdmin,
        canViewHardwareAdmin: isGlobalAdminOrSentry,
        canViewHardwareRelatedIssuesAcrossLocations: isGlobalAdmin,
        canBulkArchiveIssues: isGlobalAdmin,
        canCreateHardware: isGlobalAdmin,
        canEditNodeStatus: (currentStatus) =>
            // If a node is already set to an inactive status or has been submitted for RMA, only allow global admins to edit
            isNodeInactive(currentStatus) ||
            currentStatus === HardwareStatusCode.SubmittedForRma
                ? isGlobalAdmin
                : isAdmin,
        canSetNodeInactive: isGlobalAdmin,
        canEditAllowedFrequencies: isGlobalAdmin,
        instantReportPerms: {
            canViewDraftIssuesReport: isGlobalAdminOrSentry,
            canViewRmaSensorReport: isKcf,
            canViewFaultyHardwareReport,
        },
        canEditScheduledReportSubscribers: isAdmin,
        canDeleteScheduledReportSubscription: isAdmin,
        canAddKcfToNotificationSubscriptions: isGlobalAdminOrSentry,
        scheduledReportSubscriptionsPerms: {
            canViewFaultyHardwareReport,
        },
        canViewNotificationSubscriptionsSettings: true,
        canViewGroupNotificationSubscriptions: isAdmin,
        canDeleteUserNotificationSubscription: isAdmin,
        canCreateNotificationSubscriptionsForOthers: isAdmin,
        canViewIndustries: isGlobalAdmin,
        // TODO: Change to isAdmin || isKcf once we have more information on use cases
        canViewContainerManagement: isGlobalAdmin,
        // TODO: Change to isAdmin once we have more information on use cases
        canEditContainerManagement: isGlobalAdmin,
        canGrantAccessToAll: isGlobalAdmin,
        canViewKcfUsers: isKcf,
        canEditHubName: isAdmin,
        canEditHubAdmin: isGlobalAdmin,
        canViewIntegrations: isAdmin,
        canCreateIntegrations: isGlobalAdminOrSentry,

        useCanEditServiceDefinition: (definition: IEdgeDefinitionResponse | undefined) =>
            useOwnedEntityEditPermissions(
                user,
                useMemo(
                    () =>
                        definition
                            ? {
                                  id: definition.Owner.Id,
                                  type: definition.Owner.OwnerType,
                              }
                            : undefined,
                    [definition],
                ),
            ),

        useCanEditImageRepository: (
            imageRepository: IEdgeImageRepositoryResponse | undefined,
        ) =>
            useOwnedEntityEditPermissions(
                user,
                useMemo(
                    () =>
                        imageRepository
                            ? {
                                  id: imageRepository.Owner.OwnerId,
                                  type: imageRepository.Owner.OwnerType,
                              }
                            : undefined,
                    [imageRepository],
                ),
            ),

        canViewKcfInfo: isKcf,
        canEditKcfInfo: isGlobalAdmin,
        canExportChartImages: isGlobalAdminOrSentry,
        canViewWaterfallChart: true,
        canCreateCustomMarker: isAdmin,
        canViewMachineHealthNotificationSubscriptionCategory: (
            category: MachineHealthEventType,
        ) => {
            if (isGlobalAdminOrSentry) {
                return true;
            }

            return (
                category !== MachineHealthEventType.DraftIssueCreate ||
                (location !== undefined &&
                    location.Settings.DraftIssuesAvailableToCustomerAdmins &&
                    isAdmin)
            );
        },
        issue: {
            canViewHardwareIssues: true,
            canViewInternalIssues: isKcf,
            canEditInternalIssue: isGlobalAdminOrSentry,
            canBatchArchive: isGlobalAdminOrSentry,
            canViewDraftIssues: (mode) =>
                isGlobalAdminOrSentry ||
                (mode === 'machineHealth' &&
                    isAdmin &&
                    location !== undefined &&
                    location.Settings.DraftIssuesAvailableToCustomerAdmins),
            canEditIssue: (issue) => {
                // if saas or sentry and issue is not closed
                if (isGlobalAdminOrSentry) {
                    return [
                        IssueStatusType.Draft,
                        IssueStatusType.New,
                        IssueStatusType.Open,
                    ].includes(issue.IssueStatus.StatusType);
                }
                // if regular user and issue is open
                if (globalRole === ApplicationRoleCode.User) {
                    return issue.IssueStatus.StatusType === IssueStatusType.Open;
                }
                return false;
            },
            canEditIssueDescription: (issue) =>
                // saas or sentry and issue is draft
                isGlobalAdminOrSentry &&
                issue.IssueStatus.StatusType === IssueStatusType.Draft,
            canEditInitialRecommendation: (issue) =>
                // saas or sentry and issue is draft
                isGlobalAdminOrSentry &&
                issue.IssueStatus.StatusType === IssueStatusType.Draft,
            canEditComment: (comment) =>
                !!comment.CreatedBy &&
                !!user &&
                // owner of the comment
                (comment.CreatedBy.UserId === user.Id ||
                    // a global admin or sentry user editing a desk chat comment
                    (isGlobalAdminOrSentry && isDeskChatUser(comment.CreatedBy.UserId))),
            canDeleteComment: (comment) =>
                !!comment.CreatedBy &&
                !!user &&
                // owner of the comment
                (comment.CreatedBy.UserId === user.Id ||
                    // a desk chat comment...
                    (isDeskChatUser(comment.CreatedBy.UserId) &&
                        // is being deleted by a global admin or sentry user
                        (isGlobalAdminOrSentry ||
                            // is being deleted by the user the comment was in reply to
                            ('ReplyToComment' in comment &&
                                !!comment.ReplyToComment &&
                                comment.ReplyToComment.ReplyToUser.UserId === user.Id)))),
            canEditActivityChartDataAnnotations: (chartData) =>
                // saas, sentry, or owner of the chart data
                isGlobalAdminOrSentry ||
                (chartData.CreatedBy?.UserId
                    ? chartData.CreatedBy.UserId === user?.Id
                    : false),
            canDeleteChartDataFromActivity: (chartData) =>
                // owner of the chart data
                chartData.CreatedBy?.UserId
                    ? chartData.CreatedBy.UserId === user?.Id
                    : false,
            canEditInitialChartDataAnnotations: (issue) =>
                // saas or sentry and the issue hasn't been deleted (removed)
                isGlobalAdminOrSentry &&
                issue.IssueStatus.StatusType !== IssueStatusType.Removed,
            canDeleteInitialChartData: (issue) =>
                // saas or sentry and issue is draft
                isGlobalAdminOrSentry &&
                issue.IssueStatus.StatusType === IssueStatusType.Draft,
            canViewHealthRangeTagging: (issue) =>
                // saas or sentry and issue status in allowed list
                isGlobalAdminOrSentry &&
                allowedHealthRangeIssueStatuses.includes(issue.IssueStatus.StatusType),
            canFilterByHealthRanges: isGlobalAdminOrSentry,
            canIndicateFaultOnChartData: isGlobalAdminOrSentry,
            canVerifySavings: !isKcf,
            canSetValueTags: isKcf,
            canAutoOpenIssue: !isKcf,
        },
        canSwitchEnvironment: isKcf,
        canUseQaUserSwitcher: isGlobalAdmin,
        canEditLocationFeaturePermissions: isGlobalAdmin,
        canManageUserTags: isGlobalAdmin,
        canViewClientSoftware: isGlobalAdmin,
        get canInteractWithDeskChat() {
            if (!location) {
                return false;
            }
            const ef = new Set(location.LocationPermissions.DeskChatAutoReplyEnabledFor);

            return (
                (ef.has(FeatureUserGroup.User) &&
                    (hasRoleAtLocation || hasRoleAtCorporation)) ||
                (ef.has(FeatureUserGroup.Admin) && isAdmin) ||
                (ef.has(FeatureUserGroup.Sales) &&
                    globalRole === ApplicationRoleCode.Sales) ||
                (ef.has(FeatureUserGroup.Sentry) && isSentry) ||
                (ef.has(FeatureUserGroup.GlobalAdmin) && isGlobalAdmin)
            );
        },
        canCreateLocationFilter: isAdmin,
        canViewOtherUsersActivity: isGlobalAdminOrSentry,
    };

    return permissionsContext;
}

export { checkIsKcf as isKcf };

type SimpleEntityOwner = {
    id: string;
    type: EdgeComputeOwnerType;
};

const useOwnedEntityEditPermissions = (
    user: Readonly<ICurrentUserResponse> | undefined,
    owner: SimpleEntityOwner | undefined,
) => {
    const [corporationId, setCorporationId] = useState<string | undefined>();

    const canEditOwnedEntity = useMemo(() => {
        const isGlobalAdminOrSentry =
            user?.ApplicationRole === ApplicationRoleCode.GlobalAdmin ||
            user?.ApplicationRole === ApplicationRoleCode.Sentry;

        if (owner) {
            switch (owner.type) {
                case EdgeComputeOwnerType.Corporation:
                    return (
                        isGlobalAdminOrSentry || hasCorporationAdminRole(user, owner.id)
                    );
                case EdgeComputeOwnerType.Location:
                    return (
                        isGlobalAdminOrSentry ||
                        hasLocationAdminRole(user, owner.id) ||
                        hasCorporationAdminRole(user, corporationId)
                    );
                default:
                    return isGlobalAdminOrSentry;
            }
        }
        return false;
    }, [corporationId, owner, user]);

    useEffect(() => {
        if (owner && owner.type === EdgeComputeOwnerType.Location) {
            sdClient
                .getLocation({ locationId: owner.id })
                .then((result) => setCorporationId(result.Corporation.CorporationId))
                .catch((error: unknown) => {
                    const isNetworkError =
                        error instanceof TypeError &&
                        (error.message.includes('fetch') ||
                            error.message.includes('Load failed'));

                    if (isNetworkError) {
                        sentryAddBreadcrumb({
                            category: 'permissions.error',
                            message: 'Failed to fetch location for permission check',
                            level: 'warning',
                            data: { locationId: owner.id, error: String(error) },
                        });
                    } else {
                        // Non-network errors (403, 500, auth) should be visible in Sentry
                        sentryCaptureMessage({
                            message: 'Failed to fetch location for permission check',
                            level: 'error',
                            context: {
                                permissionCheck: {
                                    locationId: owner.id,
                                    error: String(error),
                                },
                            },
                        });
                    }
                });
        } else {
            setCorporationId(undefined);
        }
    }, [owner]);

    return canEditOwnedEntity;
};

type UseOrganizationPermissionsReturn = Readonly<{
    adminCorporationIds: Set<string> | undefined;
    adminLocationIds: Set<string> | undefined;
}>;

const useOrganizationPermissions = (): UseOrganizationPermissionsReturn & {
    user: ICurrentUserResponse | undefined;
} => {
    const { user } = useUser();

    return { ...useOrganizationPermissionsForUser({ user }), user };
};

export const getOrganizationPermissionsForUser = (
    user: ICurrentUserResponse | IUserResponse | undefined,
): UseOrganizationPermissionsReturn => {
    const corporations = user?.Corporations.filter(
        (corporation) => corporation.CorporationUserRole === CorporationUserRole.Admin,
    ).map(({ CorporationId }) => CorporationId);
    const currentUserAdminCorporationIds = corporations
        ? new Set(corporations)
        : undefined;

    const locations = user?.Locations.filter(
        (location) => location.LocationUserRole === LocationUserRole.Admin,
    ).map(({ LocationId }) => LocationId);
    const currentUserAdminLocationIds = locations ? new Set(locations) : undefined;

    return {
        adminCorporationIds: currentUserAdminCorporationIds,
        adminLocationIds: currentUserAdminLocationIds,
    };
};

export const useOrganizationPermissionsForUser = ({
    user,
}: {
    user: ICurrentUserResponse | IUserResponse | undefined;
}): UseOrganizationPermissionsReturn =>
    useMemo(() => getOrganizationPermissionsForUser(user), [user]);

export const usePermissionsCheck = <T>(
    checkFn: (
        resource: T,
        userRole: ApplicationRoleCode,
        adminCorporationIds: Set<string>,
        adminLocationIds: Set<string>,
    ) => boolean,
) => {
    const { user, adminCorporationIds, adminLocationIds } = useOrganizationPermissions();

    return {
        check: useMemo(
            () =>
                user && adminCorporationIds && adminLocationIds
                    ? (resource: T) =>
                          checkFn(
                              resource,
                              user.ApplicationRole,
                              adminCorporationIds,
                              adminLocationIds,
                          )
                    : () => false,
            [checkFn, user, adminCorporationIds, adminLocationIds],
        ),
    };
};
